import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { UserProfile } from '../lib/types';

const ONBOARDING_KEY = 'perch_onboarding_completed';

/**
 * Manages onboarding state with server-side source of truth.
 * localStorage is a fast cache; `user_profiles.onboarding_completed_at` is canonical.
 * On complete, writes to both localStorage (instant) and DB (async).
 */
export function useOnboardingState(profile: UserProfile | null, userId: string | undefined) {
  const [isComplete, setIsComplete] = useState(() => {
    // Server-side source of truth takes precedence
    if (profile?.onboarding_completed_at) return true;
    // Fall back to localStorage for fast initial render
    try {
      return localStorage.getItem(ONBOARDING_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const complete = useCallback(() => {
    // Instant local update
    try {
      localStorage.setItem(ONBOARDING_KEY, 'true');
    } catch {
      // localStorage unavailable
    }
    setIsComplete(true);

    // Async DB update (best-effort)
    if (userId) {
      supabase
        .from('user_profiles')
        .update({ onboarding_completed_at: new Date().toISOString() })
        .eq('id', userId)
        .then(() => {/* best-effort */});
    }
  }, [userId]);

  return { isOnboardingComplete: isComplete, completeOnboarding: complete };
}
